// SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
// Copyright (C) 2024 (original work) Open Assessment Technologies SA;
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License

import { get } from 'svelte/store';
import config from '@/config';
import { log } from '@/core/utils';
import { user as currentUserStore } from '@/stores/auth.store';
import { AuthError } from '@/errors';

/**
 * Guards determine whether a given path will be handled by the route controller or not and allow it
 * @callback Guard
 * @param {Function} next The callback that must be called to execute the route controller
 * @param {typeof import('../router.js').default} router
 * @returns {Promise}
 */

/**
 *
 * @param {Array<Object>} permissions
 * @param {Array<Object>} restrictions
 * @param {Array<Object>} allowedPermissions
 * @returns {boolean}
 */
export const isActionAllowed = function (permissions, restrictions, allowedPermissions = []) {
    if (!allowedPermissions[Symbol.iterator]) {
        log.error('Allowed permissions must be an array');
        return false;
    }

    if (allowedPermissions.length === 0) {
        return true;
    }

    if (!permissions) {
        return false;
    }

    for (const allowedPermission of allowedPermissions) {
        for (const permission of permissions) {
            if (
                (permission.resource === allowedPermission.resource || permission.resource === 'any') &&
                (permission.scopes.includes('all') ||
                    allowedPermission.scopes.every(scope => permission.scopes.includes(scope)))
            ) {
                if (restrictions) {
                    for (const restriction of restrictions) {
                        if (
                            restriction.resource === allowedPermission.resource &&
                            allowedPermission.scopes.every(scope => restriction.scopes.includes(scope))
                        ) {
                            return false;
                        }
                    }
                }
                return true;
            }
        }
    }

    return false;
};

/**
 * Check if the current user has the required permissions to perform an action
 * @param {Array<Object>} allowedPermissions
 * @returns {boolean}
 */
export const isActionAllowedForCurrentUser = function (allowedPermissions) {
    const currentUser = get(currentUserStore);
    return isActionAllowed(currentUser?.permissions, currentUser?.restrictions, allowedPermissions);
};

/**
 *
 * @param {Array<string>} allowedPermissions
 * @param {boolean} allowAnonymous
 * @param {boolean} allowAll
 * @returns {Guard}
 */
export const getPermissionsGuard = (allowedPermissions, allowAnonymous, allowAll) => async (next, router) => {
    try {
        await currentUserStore.updateTokenData();
        const user = get(currentUserStore);

        if (!user) {
            if (allowAnonymous) {
                return await next();
            }
            log.warn('User is not allowed to access this page');
            await router.redirect({
                backPath: window.location.pathname + window.location.search,
                url: config.routes.login.main
            });
            return;
        }

        if (!allowAll && !isActionAllowedForCurrentUser(allowedPermissions)) {
            throw new AuthError('User is not allowed to access this page');
        }

        return await next();
    } catch (err) {
        log.error(err);
        await router.redirect(config.routes.error);
    }
};
